import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpc } from "../../trpc";
import {
  buildRoleAssignmentReviewSignature,
  confirmScriptRoleReviewInputSchema,
  validateRoleReviewSelections,
  type ConfirmScriptRoleReviewInput,
  type ScriptRoleReviewChange,
  type ScriptRoleReviewMemberSnapshot,
  type ScriptRoleReviewSelection,
} from "../../../shared/scriptRoleReview";
import type { RoleAssignmentItem } from "../../../shared/scriptRoleAssignment";
import type { AssignmentResult } from "./types";
import {
  loadScriptRoleReviewDraft,
  removeScriptRoleReviewDraft,
  saveScriptRoleReviewDraft,
  type ReviewDraftStorage,
} from "./scriptRoleReviewDraftStorage";

export interface ScriptAssignmentReviewConfirmResult {
  reviewId: string;
  updatedCells: number;
  changedRoleCount: number;
  alreadyCompleted: boolean;
}

interface ScriptAssignmentReviewSession {
  sessionKey: string;
  reviewId: string;
  reviewStartedAt: string;
  originalAssignmentSignature: string;
  originalAssignments: readonly RoleAssignmentItem[];
  draftSelections: Readonly<Record<string, string>>;
  changeHistory: readonly ScriptRoleReviewChange[];
}

export interface UseScriptAssignmentReviewOptions {
  open: boolean;
  assignmentResult: AssignmentResult;
  sourceFingerprint: string;
  sheetTitle: string;
  overwriteMode: boolean;
  imageTextsByFileToken: Record<string, string>;
  onConfirmStart?: () => void;
  onConfirmed: (result: ScriptAssignmentReviewConfirmResult) => void;
  onConfirmError?: (error: Error) => void;
}

function cloneAndFreezeAssignments(assignments: RoleAssignmentItem[]) {
  const cloned = assignments.map((assignment) => {
    const scoreBreakdown = assignment.scoreBreakdown.map((dimension) =>
      Object.freeze({ ...dimension })
    );
    return Object.freeze({
      ...assignment,
      scoreBreakdown: Object.freeze(scoreBreakdown),
    }) as RoleAssignmentItem;
  });
  return Object.freeze(cloned) as readonly RoleAssignmentItem[];
}

function cloneMember(member: ScriptRoleReviewMemberSnapshot): ScriptRoleReviewMemberSnapshot {
  return {
    ...member,
    specificHealthTags: [...member.specificHealthTags],
    specificProductTags: [...member.specificProductTags],
    specificFamilyTags: [...member.specificFamilyTags],
    specificStoryTags: [...member.specificStoryTags],
    specificToneTags: [...member.specificToneTags],
    forbiddenTopics: [...member.forbiddenTopics],
  };
}

function normalizeError(error: unknown, fallback: string) {
  return error instanceof Error ? error : new Error(fallback);
}

function getDraftStorage(): ReviewDraftStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function useScriptAssignmentReview(options: UseScriptAssignmentReviewOptions) {
  const {
    open,
    assignmentResult,
    sourceFingerprint,
    sheetTitle,
    overwriteMode,
    imageTextsByFileToken,
    onConfirmStart,
    onConfirmed,
    onConfirmError,
  } = options;

  const originalAssignmentSignature = useMemo(
    () => buildRoleAssignmentReviewSignature(assignmentResult.assignments),
    [assignmentResult.assignments]
  );
  const sessionKey = `${assignmentResult.spreadsheetToken}:${assignmentResult.sheetId}:${assignmentResult.group}:${sourceFingerprint}:${originalAssignmentSignature}`;

  const [session, setSession] = useState<ScriptAssignmentReviewSession | null>(null);
  const [members, setMembers] = useState<ScriptRoleReviewMemberSnapshot[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<Error | null>(null);
  const [confirmError, setConfirmError] = useState<Error | null>(null);
  const [draftStorageWarning, setDraftStorageWarning] = useState<string | null>(null);
  const [draftRestoreWarning, setDraftRestoreWarning] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const activeSessionKeyRef = useRef<string | null>(null);
  const sessionRef = useRef<ScriptAssignmentReviewSession | null>(null);
  const submittingRef = useRef(false);

  const persistDraftSession = useCallback((current: ScriptAssignmentReviewSession) => {
    const storage = getDraftStorage();
    if (!storage) {
      setDraftStorageWarning("浏览器无法保存审查草稿，刷新页面会丢失本次修改");
      return;
    }
    try {
      saveScriptRoleReviewDraft({
        storage,
        sessionKey: current.sessionKey,
        snapshot: {
          reviewId: current.reviewId,
          reviewStartedAt: current.reviewStartedAt,
          originalAssignmentSignature: current.originalAssignmentSignature,
          draftSelections: { ...current.draftSelections },
          changeHistory: current.changeHistory.map((change) => ({ ...change })),
        },
      });
      setDraftStorageWarning(null);
    } catch {
      setDraftStorageWarning("审查草稿保存失败，刷新页面会丢失本次修改");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (activeSessionKeyRef.current === sessionKey) return;

    activeSessionKeyRef.current = sessionKey;
    submittingRef.current = false;
    setSubmitting(false);
    setConfirmError(null);
    setDraftRestoreWarning(null);
    const restoredDraft = (() => {
      const storage = getDraftStorage();
      if (!storage) {
        setDraftStorageWarning("浏览器无法保存审查草稿，刷新页面会丢失本次修改");
        return null;
      }
      try {
        const result = loadScriptRoleReviewDraft({
          storage,
          sessionKey,
          originalAssignmentSignature,
          tempRoleIds: assignmentResult.tempRoles.map((role) => role.tempRoleId),
        });
        if (result.status === "invalid") {
          setDraftRestoreWarning(`${result.reason}，已从当前匹配重新建立草稿`);
          return null;
        }
        return result.status === "restored" ? result.snapshot : null;
      } catch {
        setDraftStorageWarning("审查草稿读取失败，本次修改尚未获得刷新保护");
        return null;
      }
    })();
    const nextSession: ScriptAssignmentReviewSession = {
      sessionKey,
      reviewId: restoredDraft?.reviewId ?? crypto.randomUUID(),
      reviewStartedAt: restoredDraft?.reviewStartedAt ?? new Date().toISOString(),
      originalAssignmentSignature,
      originalAssignments: cloneAndFreezeAssignments(assignmentResult.assignments),
      draftSelections: Object.freeze(
        restoredDraft?.draftSelections ??
          Object.fromEntries(
            assignmentResult.assignments.map((assignment) => [
              assignment.tempRoleId,
              assignment.wxid,
            ])
          )
      ),
      changeHistory: Object.freeze(
        (restoredDraft?.changeHistory ?? []).map((change) => Object.freeze({ ...change }))
      ),
    };
    sessionRef.current = nextSession;
    setSession(nextSession);
    persistDraftSession(nextSession);
  }, [
    assignmentResult.assignments,
    open,
    originalAssignmentSignature,
    persistDraftSession,
    sessionKey,
  ]);

  useEffect(() => {
    if (!open || !assignmentResult.group) return;

    let cancelled = false;
    setMembersLoading(true);
    setMembersError(null);
    void trpc.atmosphereMember.list
      .query({ group: assignmentResult.group })
      .then((items) => {
        if (cancelled) return;
        setMembers(items.map((item) => cloneMember(item)));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setMembers([]);
        setMembersError(normalizeError(error, "加载气氛员资料失败"));
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [assignmentResult.group, open, sessionKey]);

  const activeSession = session?.sessionKey === sessionKey ? session : null;

  const originalAssignmentByRoleId = useMemo(
    () =>
      new Map(
        (activeSession?.originalAssignments ?? []).map((assignment) => [
          assignment.tempRoleId,
          assignment,
        ])
      ),
    [activeSession?.originalAssignments]
  );

  const memberByWxid = useMemo(
    () => new Map(members.map((member) => [member.wxid, member])),
    [members]
  );

  const memberSelectionIssueByWxid = useMemo(() => {
    const wxidsByNumber = new Map<string, string[]>();
    for (const member of members) {
      const number = member.number.trim();
      if (!number) continue;
      const wxids = wxidsByNumber.get(number) ?? [];
      wxids.push(member.wxid);
      wxidsByNumber.set(number, wxids);
    }

    const issues = new Map<string, string>();
    for (const member of members) {
      const number = member.number.trim();
      if (!number) {
        issues.set(member.wxid, "未填写编号");
      } else if (!/^\d+$/.test(number)) {
        issues.set(member.wxid, "编号必须为纯数字");
      } else if ((wxidsByNumber.get(number)?.length ?? 0) !== 1) {
        issues.set(member.wxid, `编号 ${number} 在当前分组中重复`);
      }
    }
    return issues;
  }, [members]);

  const selectableMemberWxids = useMemo(
    () => members.filter((member) => !memberSelectionIssueByWxid.has(member.wxid)).map((member) => member.wxid),
    [memberSelectionIssueByWxid, members]
  );

  const finalSelections = useMemo<ScriptRoleReviewSelection[]>(() => {
    if (!activeSession) return [];
    return assignmentResult.tempRoles.flatMap((role) => {
      const wxid = activeSession.draftSelections[role.tempRoleId];
      return wxid ? [{ tempRoleId: role.tempRoleId, wxid }] : [];
    });
  }, [activeSession, assignmentResult.tempRoles]);

  const changedTempRoleIds = useMemo(() => {
    const changed = new Set<string>();
    if (!activeSession) return changed;
    for (const role of assignmentResult.tempRoles) {
      if (
        activeSession.draftSelections[role.tempRoleId] &&
        !activeSession.originalAssignments.some(
          (assignment) => assignment.tempRoleId === role.tempRoleId
        )
      ) {
        changed.add(role.tempRoleId);
      }
    }
    for (const assignment of activeSession.originalAssignments) {
      if (activeSession.draftSelections[assignment.tempRoleId] !== assignment.wxid) {
        changed.add(assignment.tempRoleId);
      }
    }
    return changed;
  }, [activeSession, assignmentResult.tempRoles]);

  const selectionValidation = useMemo(() => {
    const validation = validateRoleReviewSelections({
        tempRoleIds: assignmentResult.tempRoles.map((role) => role.tempRoleId),
        selections: finalSelections,
        validMemberWxids: selectableMemberWxids,
      });
    if (validation.ok) return validation;

    const invalidSelection = finalSelections.find((selection) =>
      memberSelectionIssueByWxid.has(selection.wxid)
    );
    const issue = invalidSelection
      ? memberSelectionIssueByWxid.get(invalidSelection.wxid)
      : undefined;
    return issue && invalidSelection
      ? { ok: false as const, error: `角色 ${invalidSelection.tempRoleId} 的气氛员不可写入：${issue}` }
      : validation;
  }, [
    assignmentResult.tempRoles,
    finalSelections,
    memberSelectionIssueByWxid,
    selectableMemberWxids,
  ]);

  const changeSelection = useCallback(
    (tempRoleId: string, wxid: string) => {
      setConfirmError(null);
      const current = sessionRef.current;
      if (!current || current.sessionKey !== sessionKey) return;
      const fromWxid = current.draftSelections[tempRoleId];
      if (fromWxid === wxid) return;

      const nextSession: ScriptAssignmentReviewSession = {
        ...current,
        draftSelections: Object.freeze({
          ...current.draftSelections,
          [tempRoleId]: wxid,
        }),
        changeHistory: fromWxid
          ? Object.freeze([
              ...current.changeHistory,
              Object.freeze({
                tempRoleId,
                fromWxid,
                toWxid: wxid,
                changedAt: new Date().toISOString(),
              }),
            ])
          : current.changeHistory,
      };
      sessionRef.current = nextSession;
      setSession(nextSession);
      persistDraftSession(nextSession);
    },
    [persistDraftSession, sessionKey]
  );

  const restoreOriginalSelection = useCallback(
    (tempRoleId: string) => {
      const original = originalAssignmentByRoleId.get(tempRoleId);
      if (original) changeSelection(tempRoleId, original.wxid);
    },
    [changeSelection, originalAssignmentByRoleId]
  );

  const confirm = useCallback(async () => {
    if (submittingRef.current) return;
    if (!activeSession) {
      const error = new Error("审查会话尚未准备完成");
      setConfirmError(error);
      onConfirmError?.(error);
      return;
    }
    if (membersLoading) {
      const error = new Error("气氛员资料仍在加载，请稍候");
      setConfirmError(error);
      onConfirmError?.(error);
      return;
    }
    if (membersError) {
      setConfirmError(membersError);
      onConfirmError?.(membersError);
      return;
    }
    if (!selectionValidation.ok) {
      const error = new Error(selectionValidation.error);
      setConfirmError(error);
      onConfirmError?.(error);
      return;
    }

    const payloadResult = confirmScriptRoleReviewInputSchema.safeParse({
      reviewId: activeSession.reviewId,
      spreadsheetToken: assignmentResult.spreadsheetToken,
      sheetId: assignmentResult.sheetId,
      sheetTitle,
      group: assignmentResult.group,
      sourceFingerprint,
      overwriteMode,
      originalAssignmentSignature: activeSession.originalAssignmentSignature,
      imageTextsByFileToken,
      reviewStartedAt: activeSession.reviewStartedAt,
      finalSelections,
      changeHistory: activeSession.changeHistory,
    });
    if (!payloadResult.success) {
      const error = new Error(
        payloadResult.error.issues[0]?.message ?? "审查提交数据校验失败"
      );
      setConfirmError(error);
      onConfirmError?.(error);
      return;
    }
    const payload: ConfirmScriptRoleReviewInput = payloadResult.data;

    submittingRef.current = true;
    setSubmitting(true);
    setConfirmError(null);
    onConfirmStart?.();

    try {
      const result: ScriptAssignmentReviewConfirmResult =
        await trpc.feishuSheets.confirmRoleAssignmentReview.mutate(payload);
      const storage = getDraftStorage();
      if (storage) {
        try {
          removeScriptRoleReviewDraft(storage, sessionKey);
        } catch {
          // Confirmation already succeeded; server idempotency rejects stale retries.
        }
      }
      onConfirmed(result);
    } catch (error: unknown) {
      const normalized = normalizeError(error, "确认并写入飞书失败");
      setConfirmError(normalized);
      onConfirmError?.(normalized);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    assignmentResult.group,
    assignmentResult.sheetId,
    assignmentResult.spreadsheetToken,
    finalSelections,
    imageTextsByFileToken,
    membersError,
    membersLoading,
    onConfirmError,
    onConfirmStart,
    onConfirmed,
    overwriteMode,
    selectionValidation,
    activeSession,
    sheetTitle,
    sourceFingerprint,
    sessionKey,
  ]);

  const combinedDraftWarning = [draftRestoreWarning, draftStorageWarning]
    .filter((warning): warning is string => Boolean(warning))
    .join("；");

  return {
    reviewId: activeSession?.reviewId ?? "",
    reviewStartedAt: activeSession?.reviewStartedAt ?? "",
    originalAssignments: activeSession?.originalAssignments ?? [],
    originalAssignmentByRoleId,
    draftSelections: activeSession?.draftSelections ?? {},
    finalSelections,
    changeHistory: activeSession?.changeHistory ?? [],
    changedTempRoleIds,
    changedRoleCount: changedTempRoleIds.size,
    members,
    memberByWxid,
    memberSelectionIssueByWxid,
    selectableMemberWxids,
    membersLoading,
    membersError,
    confirmError,
    draftStorageWarning: combinedDraftWarning || null,
    submitting,
    selectionValidation,
    canConfirm:
      Boolean(activeSession) &&
      !membersLoading &&
      !membersError &&
      selectionValidation.ok &&
      !submitting,
    changeSelection,
    restoreOriginalSelection,
    confirm,
  };
}
